import React from "react";
import { getBucketColor, getBucketLabel } from "./bucketColors";
import type { ConversationNav } from "../../../hooks/data-fetching/useConversationPreviews";

interface ConversationRow {
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  time_value: number | null;
  time_label: string;
  timestamp: string;
  was_ignored?: boolean;
  highlightTimestamp?: string;
  anchorTimestamp?: string;
}

interface BucketGroup {
  bucket: string;
  conversations: ConversationRow[];
}

interface ConversationProofListProps {
  groups: BucketGroup[];
  timezone: string;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string },
    nav?: ConversationNav
  ) => void;
}

function formatTimestamp(iso: string, timezone: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: timezone }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: timezone })
  );
}

export const ConversationProofList: React.FC<ConversationProofListProps> = ({
  groups,
  timezone,
  onConversationClick,
}) => {
  if (groups.length === 0 || groups.every((g) => g.conversations.length === 0)) {
    return <p className="token-14 text-glass-muted py-4 text-center">No data for this metric.</p>;
  }

  // Swipe-nav siblings: the FULL flat list in screen order (all buckets, top to
  // bottom), so opening any row lets the reader swipe every conversation shown.
  // Same convention as ProofGridView.onHeaderClick (P1) — the grid path was wired
  // but this default-list path was not, so the proof reader opened nav-less here.
  // No initialPreview: these rows carry no messages array, so the neighbor cards
  // are filled by the reader's own F1/F4 prefetch, exactly like the proof grid.
  const flatSiblings = groups.flatMap((g) =>
    g.conversations.map((c) => ({
      conversationId: c.conversation_id,
      contactName: c.contact_name || "Unknown",
      leadInstagram: c.lead_instagram ?? null,
    }))
  );
  // Global index per group = sum of prior groups' lengths, so the reader's
  // position counter (N / M) counts across the whole visible list, not per bucket.
  // Pure (no render-time mutation) — buckets count is tiny, so O(n^2) is free.
  const groupOffsets = groups.map((_, gi) =>
    groups.slice(0, gi).reduce((sum, g) => sum + g.conversations.length, 0)
  );

  return (
    <div className="space-y-4">
      {groups.map((group, gi) => (
        <div key={group.bucket}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-3 h-3 rounded-sm ${getBucketColor(group.bucket)}`} />
            <h5 className="token-11 font-semibold text-glass-secondary uppercase tracking-wide">
              {getBucketLabel(group.bucket)} ({group.conversations.length})
            </h5>
          </div>
          <div className="space-y-1">
            {group.conversations.map((conv, idx) => (
              <div
                key={`${conv.conversation_id}-${conv.timestamp || idx}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-black/[0.03] cursor-pointer transition-colors group"
                onClick={() =>
                  onConversationClick(
                    conv.conversation_id,
                    conv.contact_name || "Unknown",
                    conv.lead_instagram,
                    conv.highlightTimestamp
                      ? { highlight: conv.highlightTimestamp, anchor: conv.anchorTimestamp }
                      : undefined,
                    flatSiblings.length > 1
                      ? {
                          siblings: flatSiblings,
                          index: groupOffsets[gi] + idx,
                          hasMore: false,
                        }
                      : undefined
                  )
                }
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="token-14 font-medium text-glass truncate">
                      {conv.contact_name || "Unknown"}
                    </span>
                    {conv.lead_instagram && (
                      <span className="token-11 text-glass-muted truncate">
                        @{conv.lead_instagram}
                      </span>
                    )}
                    {conv.was_ignored && (
                      <span className="token-11 bg-ds-red/15 text-ds-red px-1.5 py-0.5 rounded font-medium">
                        Ignored
                      </span>
                    )}
                  </div>
                </div>
                <span className="token-14 font-semibold text-glass-secondary shrink-0">
                  {conv.time_label}
                </span>
                {conv.timestamp && (
                  <span className="token-11 text-glass-muted shrink-0">
                    {formatTimestamp(conv.timestamp, timezone)}
                  </span>
                )}
                <svg
                  className="w-4 h-4 text-glass-muted group-hover:text-glass-secondary shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
